import {
  type DragEndEvent,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flatten, groupBy, isEqual, orderBy } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAccountContext } from 'utils/account/AccountContext';
import { REPLACE_DRAGULA } from 'utils/release-toggles';

import { ClientDataTableOLD } from './ClientDataTableOLD';
import { DraggableRow, StyledTableV5 as Table } from './DataTable.style';
import { EmptyState } from './EmptyState';
import { customTableRowClassName } from './utils';

import { type DataTableProps, DataTableContext } from './index';

const expandedRowKeyClassNamePrefix = 'expanded-row-id-';

interface ClientDataTableProps extends Omit<DataTableProps, 'title'> {
  draggable?: boolean;
  onDragUpdate?: (orderedItems: Record<string, any>[]) => void;
  onTableChange?: (pagination: any, filters: any, sorter: any, extra: any) => void;
  isPrioritizing?: boolean;
  handleOrderUpdate?: (orderedItems: Record<string, any>[]) => void;
}

/**
 * TODO: when cleaning up `rtv1:replace_dragula`, delete this, rename
 * `_ClientDataTable` to `ClientDataTable` and export that directly
 */
export const ClientDataTable: React.FunctionComponent<ClientDataTableProps> = props => {
  const { hasReleaseToggle } = useAccountContext();
  if (hasReleaseToggle(REPLACE_DRAGULA)) {
    return <_ClientDataTable {...props} />;
  }

  return <ClientDataTableOLD {...props} />;
};

const _ClientDataTable: React.FunctionComponent<ClientDataTableProps> = ({
  data,
  draggable,
  handleOrderUpdate,
  isPrioritizing,
  loading,
  onDragUpdate,
  onTableChange,
  rowKey,
  columns,
  ...props
}) => {
  const { filteredInfo, multiSortDef, updatePagination, initialPagination } =
    useContext(DataTableContext) || {};
  const [draggingId, setDraggingId] = useState<string>();

  /**
   * Some columns control filters for multiple properties, e.g. damage severity and critical
   * status. See ./README for detail.
   *
   * In these cases, the column definition's `onFilter` cannot be applied directly. The filters
   * are grouped by property here, and their filter functions are pulled out of the column
   * definition's `onFilter` or `additionalFilterFunctions`, and applied to the data before it is
   * passed to the table component.
   */
  const applyMultiFilters = useCallback(
    (data: Record<string, any>[]): Record<string, any>[] => {
      const columnMultiFilters = flatten(
        Object.values(filteredInfo ?? {}).filter((values: any[]) =>
          values?.[0]?.hasOwnProperty('key')
        )
      );
      const groupedMultiFilters = Object.entries(groupBy(columnMultiFilters, 'key')).map(
        ([k, v]) => [k, v.map(({ value }) => value)]
      );

      if (groupedMultiFilters.length) {
        return (data ?? []).reduce((filteredRows: Record<string, any>[], row) => {
          const filterResults: boolean[] = groupedMultiFilters.map(
            ([k, values]: [string, any[]]) => {
              const basePropertyFilterFunction = columns?.find(({ key }) => key === k)?.onFilter;
              const secondaryPropertyFilterFunction = columns?.find(
                ({ additionalFilterFunctions }) => !!additionalFilterFunctions?.[k]
              )?.additionalFilterFunctions?.[k];

              if (!!basePropertyFilterFunction) {
                return values.map(v => basePropertyFilterFunction(v, row)).every(r => !!r);
              } else if (!!secondaryPropertyFilterFunction) {
                return values.map(v => secondaryPropertyFilterFunction(v, row)).every(r => !!r);
              } else {
                return true;
              }
            }
          );

          if (filterResults.every(r => !!r)) {
            return [...(filteredRows ?? []), row];
          }
          return filteredRows;
        }, []) as Record<string, any>[];
      }

      return data;
    },
    [columns, filteredInfo]
  );
  const applyMultiSort = useCallback(
    (data: Record<string, any>[]): Record<string, any>[] => {
      // If there is a multiSortDef, we need to apply that mutli-column sort manually.
      const sortedData = orderBy(
        data,
        multiSortDef?.map(({ column: { get } }) => get),
        // sortDirection is by default in all caps for server-side functionality.
        // but lodash expects lowercase here.
        multiSortDef?.map(({ sortDirection }) => sortDirection.toLowerCase())
      );
      // Check to see if the contents of dataSource changed after sorting.
      const sourcesEqual = isEqual(data, sortedData);

      // If prioritizing, we need to trigger a specific callback to update the priority state
      // It's only safe to call the prioritization handleUpdate callback if we are prioritizing
      // and we have the callback
      // and the and the sort has changed the order of the contents of the list
      // and we're not currently updating using drag and drop
      if (isPrioritizing && handleOrderUpdate && !sourcesEqual) {
        handleOrderUpdate(sortedData);
      }
      return sortedData;
    },
    [handleOrderUpdate, isPrioritizing, multiSortDef]
  );

  const dataSource = useMemo(() => {
    // for most implementations, we want to remove null values.
    // Implementations that do not want this can replace a null value with empty object.
    const filteredData = applyMultiFilters((data || []).filter(Boolean));

    if (multiSortDef) {
      return applyMultiSort(filteredData);
    }

    return filteredData;
  }, [data, multiSortDef, applyMultiFilters, applyMultiSort]);

  const draggingRow = useMemo(
    () => draggingId && dataSource.find(row => row.id === draggingId),
    [draggingId, dataSource]
  );

  useEffect(
    () => {
      if (draggable) {
        updatePagination(false);
      } else {
        updatePagination(initialPagination);
      }
    },
    //intentionally omitting update/initialPagination
    [draggable] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Configuration for drag event callbacks and styling
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id && dataSource && onDragUpdate) {
      const activeIndex = dataSource.findIndex(i => i.id === active.id);

      /**
       * If the `over` row is an expandable section, find its parent using the id from the
       * `expandedRowClassName`. This prevents `overIndex` from being -1 and causing
       * unexpected behavior
       */
      const overIndex = dataSource.findIndex(i =>
        [i.id, `${expandedRowKeyClassNamePrefix}${i.id}`].includes(over?.id)
      );
      const rePrioritized = arrayMove(dataSource, activeIndex, overIndex);
      onDragUpdate(rePrioritized);
      setDraggingId(undefined);
    }
  };

  const handlePrioritizingTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any
  ) => {
    onTableChange?.(pagination, filters, sorter, extra);
    // If the table is prioritizing and the table changes, we need to pass the updated ordering of the table to the priority-handling
    // handleOrderUpdate function, so that the save button can be correctly enabled/disabled.

    if (isPrioritizing && handleOrderUpdate) {
      const orderedItems = extra.currentDataSource;
      handleOrderUpdate(orderedItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={event => setDraggingId(event.active.id)}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={(dataSource ?? []).map(i => i.id)}
        strategy={verticalListSortingStrategy}
        disabled={!isPrioritizing}
      >
        <Table
          style={draggingId ? { overflowX: 'hidden' } : {}}
          locale={{ emptyText: <EmptyState {...props} loading={!!loading} /> }}
          {...props}
          columns={columns}
          components={{
            body: { row: Row },
          }}
          expandable={{
            ...props.expandable,
            /**
             * add a class containing the parent row's key to the expanded row, which can
             * be used to identify it during drag & drop events
             */
            expandedRowClassName: (record: { key?: string; id?: string }) =>
              `${expandedRowKeyClassNamePrefix}${record.key ?? record.id}`,
          }}
          loading={loading}
          dataSource={dataSource}
          bordered={false}
          rowKey={rowKey}
          onChange={(...args) =>
            isPrioritizing ? handlePrioritizingTableChange(...args) : onTableChange?.(...args)
          }
          rowClassName={customTableRowClassName}
        />
        <DragOverlay>
          {draggingRow ? (
            <Table
              draggingTable={true}
              {...props}
              expandable={{
                ...props.expandable,
                expandedRowKeys: [],
              }}
              columns={columns}
              dataSource={[draggingRow]}
              rowKey={rowKey}
              bordered={false}
            />
          ) : null}
        </DragOverlay>
      </SortableContext>
    </DndContext>
  );
};

/**
 * Find the expanded row key classname, if it exists
 */
const getExpandedRowKeyClassName = (className: string): string | undefined => {
  const idClassName = className.split(' ').find(c => c.startsWith(expandedRowKeyClassNamePrefix));
  return idClassName;
};

const Row: React.FC<
  React.HTMLAttributes<HTMLTableRowElement> & {
    'data-row-key'?: string;
  }
> = props => {
  /**
   * sortableId is used in drag & drop logic for styling/transitions as well as in event handlers
   * (e.g. `handleDragEnd` in ClientDataTable). For normal rows, this is just the `data-row-key`, which
   * is set by the table component internally. For expanded rows, we set it to the `expandedRowClassName`
   * (including the prefix), so that it is identifiable in handlers, but unique from the parent row's
   * `sortableId` to avoid styling conflicts.
   */
  const sortableId: string | undefined =
    props['data-row-key'] ?? getExpandedRowKeyClassName(props['className'] ?? '');
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: sortableId ?? '',
  });

  return (
    <DraggableRow
      {...props}
      ref={setNodeRef}
      isDraggable={!attributes['aria-disabled']}
      transform={CSS.Translate.toString(transform)}
      {...attributes}
      {...listeners}
    />
  );
};
